/**
 * THE WIRE CONTRACT, written down once, in a place both halves compile against.
 *
 * ── Why this file exists ─────────────────────────────────────────────────────
 *
 * `api.get<T>(...)` is an unchecked CAST. The compiler is told the shape; it never
 * verifies it. So a frontend hook can declare `{ items }` against a route that returns
 * `{ projects }`, `tsc --noEmit` stays clean, both unit suites stay green, and the list
 * screen renders permanently empty with "undefined engagements" in its header. That is
 * exactly what happened — see `src/test/ui/contracts/response-envelopes.spec.ts`.
 *
 * The defect class is "two hand-written descriptions of one payload, in two files, that
 * nothing compares". This module is the single description. The frontend hooks no longer
 * name payload keys inline; they call the readers here, which:
 *
 *   · accept EVERY key the API is known to use for a given payload, and
 *   · fail LOUDLY-BUT-SAFELY (empty list, `null` detail) rather than silently producing
 *     `undefined` that renders as a confident wrong sentence.
 *
 * ── Why the readers tolerate several keys instead of demanding one ───────────
 *
 * The list routes are owned by different agents and are not ours to restructure:
 *
 *     GET /api/employees              { items, total, hasMore }   ← the target shape
 *     GET /api/projects               { projects, scope, financialsVisible }
 *     GET /api/clients                { clients }
 *     GET /api/expenses               { expenses, scope, count }
 *     GET /api/vendors                { vendors, count }
 *     GET /api/purchase-orders        { purchaseOrders, scope, count }
 *     GET /api/employees/:id/history  { employeeId, history, total }
 *
 * `/api/employees` is the convention the rest should adopt, and `LIST_ENVELOPE_TARGET`
 * records that so the standardisation has a stated target rather than a guess. Until the
 * owning agents converge on it, `readList` reads whichever key is actually present — and
 * because the alias list is declared here rather than at each call site, converging later
 * is a one-line change in one file instead of a hunt through the hooks.
 *
 * NOTHING IN HERE INFERS. `readList` will not go looking for "the first property that
 * happens to be an array": a payload whose shape changed underneath us must produce an
 * empty list and an obvious empty state, never a plausible list assembled from the wrong
 * field.
 */

// ─────────────────────────────────────────────────────────────────────────────
// List envelopes
// ─────────────────────────────────────────────────────────────────────────────

/** The normalised list shape every list screen consumes. */
export interface ListEnvelope<T> {
  items: T[];
  total: number;
  /** `false` unless the route paginates and says otherwise. */
  hasMore: boolean;
}

/** The envelope `/api/employees` uses, and the one the other list routes should adopt. */
export const LIST_ENVELOPE_TARGET = ['items', 'total', 'hasMore'] as const;

/**
 * Every key a list route is known to put its rows under, per client-side reader.
 *
 * Ordered: `items` first, so a route that converges on the target shape is picked up
 * without any other change.
 */
export const LIST_KEYS = {
  projects: ['items', 'projects'],
  clients: ['items', 'clients'],
  expenses: ['items', 'expenses'],
  vendors: ['items', 'vendors'],
  purchaseOrders: ['items', 'purchaseOrders'],
  employmentHistory: ['items', 'records', 'history'],
  /**
   * NOT YET WIRED, and deliberately recorded here rather than left undocumented.
   *
   * `/api/expenses/queue/pending` answers `{ pending: [{ workflowInstanceId, stepInstanceId,
   * stepName, dueAt, requestedAt, expense }], decideEndpoint }` — the rows are approval-step
   * WRAPPERS, not `ExpenseReport`s, so `usePendingExpenses` needs a row-shape change as well
   * as a key change and that is a bigger edit than an envelope fix. Until then the expense
   * approval queue reads `items` and renders empty.
   */
  pendingExpenses: ['items', 'pending'],
} as const satisfies Record<string, readonly string[]>;

/** Keys a route is known to use for the row count, in preference order. */
const TOTAL_KEYS = ['total', 'count'] as const;

function asRecord(body: unknown): Record<string, unknown> | null {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null;
}

/**
 * Reads a list payload under whichever of `keys` the route actually used.
 *
 * `total` falls back to the row count rather than `undefined` — a header that reads
 * "undefined engagements" is worse than one that reads the length of what is on screen.
 */
export function readList<T>(body: unknown, keys: readonly string[]): ListEnvelope<T> {
  const record = asRecord(body);
  if (record === null) return { items: [], total: 0, hasMore: false };

  let items: T[] | null = null;
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      items = value as T[];
      break;
    }
  }
  if (items === null) return { items: [], total: 0, hasMore: false };

  let total: number | null = null;
  for (const key of TOTAL_KEYS) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      total = value;
      break;
    }
  }

  return {
    items,
    total: total ?? items.length,
    hasMore: record['hasMore'] === true,
  };
}

/**
 * Reads a single-record payload under whichever of `keys` the route used, returning
 * `null` when none of them carried an object.
 *
 * `null` — not `undefined` — because the caller must be able to distinguish "the server
 * says there is no record" from "the key we asked for does not exist", and `undefined`
 * flowing into an optional chain is precisely how a screen ends up asserting something
 * false about data it never received.
 */
export function readRecord<T>(body: unknown, keys: readonly string[]): T | null {
  const record = asRecord(body);
  if (record === null) return null;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null) return value as T;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Error envelopes
// ─────────────────────────────────────────────────────────────────────────────

/** A single field-level complaint, as the Zod boundary validators emit them. */
export interface ApiFieldIssue {
  field: string;
  message: string;
  /** Machine code from Zod, e.g. `invalid_string`. Absent on hand-written issues. */
  code?: string;
}

/**
 * FLAT — `requirePermission` denials, and a handful of hand-written 404s.
 *
 * The permission fields are the reason this envelope is worth keeping rather than
 * normalising away at the source: `ForbiddenState` renders `requiredPermission` verbatim,
 * and a 403 that says only "Forbidden" leaves the user with nothing to ask for.
 */
export interface FlatApiErrorBody {
  error: string;
  code?: string;
  requiredPermission?: string;
  requiredPermissionDescription?: string;
  requiredScope?: string;
  heldScope?: string;
  issues?: ApiFieldIssue[];
  details?: Record<string, unknown>;
}

/**
 * NESTED — `securityErrorHandler`, `authErrorHandler`, `notFoundHandler`, the rate
 * limiter, every Zod validation failure, every store error and every unhandled 500.
 */
export interface NestedApiErrorBody {
  error: {
    code?: string;
    message: string;
    issues?: ApiFieldIssue[];
    requestId?: string;
    retryAfterSeconds?: number;
  };
}

export type ApiErrorBody = FlatApiErrorBody | NestedApiErrorBody;

/** Everything a client can learn from an error body, whichever envelope produced it. */
export interface NormalisedApiError {
  /** `null` when the body carried no message at all — the caller supplies a default. */
  message: string | null;
  code: string | null;
  issues: ApiFieldIssue[];
  requestId: string | null;
  requiredPermission: string | null;
  requiredPermissionDescription: string | null;
  requiredScope: string | null;
  heldScope: string | null;
  retryAfterSeconds: number | null;
  details: Record<string, unknown>;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function issuesOf(value: unknown): ApiFieldIssue[] {
  if (!Array.isArray(value)) return [];
  const issues: ApiFieldIssue[] = [];
  for (const entry of value) {
    const record = asRecord(entry);
    if (record === null) continue;
    const field = stringOrNull(record['field']) ?? stringOrNull(record['path']);
    const message = stringOrNull(record['message']);
    if (field === null || message === null) continue;
    const code = stringOrNull(record['code']);
    issues.push(code === null ? { field, message } : { field, message, code });
  }
  return issues;
}

/**
 * Flattens either envelope into one shape.
 *
 * The bug this replaces: `const message = body.error ?? body.message` — `body.error` is
 * typed `string | undefined`, but for the NESTED envelope it is an OBJECT, and
 * `new Error(object)` coerces it. Every validation failure, every 404 and every 500 in
 * the product rendered as the string "[object Object]", `code` was `null` so nothing that
 * branched on a machine code worked, and `issues` was empty so NO FORM EVER SHOWED A
 * FIELD-LEVEL MESSAGE. The backend's messages are specific and actionable by design
 * (CLAUDE.md rule 8); the client was discarding all of them.
 */
export function normaliseApiError(body: unknown): NormalisedApiError {
  const record = asRecord(body) ?? {};
  const nested = asRecord(record['error']);

  // Nested first: when `error` is an object it is the authoritative half of the body.
  const message =
    stringOrNull(record['error']) ??
    (nested === null ? null : stringOrNull(nested['message'])) ??
    stringOrNull(record['message']);

  const code =
    stringOrNull(record['code']) ?? (nested === null ? null : stringOrNull(nested['code']));

  const issues =
    issuesOf(record['issues']).length > 0
      ? issuesOf(record['issues'])
      : nested === null
        ? []
        : issuesOf(nested['issues']);

  const details = asRecord(record['details']) ?? {};
  const retryAfter = nested === null ? undefined : nested['retryAfterSeconds'];

  return {
    message,
    code,
    issues,
    requestId:
      stringOrNull(record['requestId']) ?? (nested === null ? null : stringOrNull(nested['requestId'])),
    requiredPermission:
      stringOrNull(record['requiredPermission']) ?? stringOrNull(details['requiredPermission']),
    requiredPermissionDescription: stringOrNull(record['requiredPermissionDescription']),
    requiredScope: stringOrNull(record['requiredScope']) ?? stringOrNull(details['requiredScope']),
    heldScope: stringOrNull(record['heldScope']) ?? stringOrNull(details['heldScope']),
    retryAfterSeconds: typeof retryAfter === 'number' && Number.isFinite(retryAfter) ? retryAfter : null,
    details,
  };
}
